/**
 * SequencingGroupsPage — list + create + drill-into compressor groups.
 *
 * Phase 1A scope: name + tenant_id + Pset + bandwidth + delays. Member
 * (device) management lives on the group-detail view in 1B.
 *
 * Tenant scope is enforced server-side via the recursive Handling-Company
 * resolver. Admins see all tenants; non-admin users see only their own
 * tenant ids and the create form auto-fills with the first one.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Stack, Plus, Trash, ArrowLeft, ArrowRight, X, Spinner, Lightning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  seqListGroups, seqCreateGroup, seqDeleteGroup,
} from "@/lib/sequencingApi";
import { useT } from "@/lib/i18n";
import { TourHelpButton } from "@/components/GuidedTour";
import GroupsListTour from "@/components/sequencing/GroupsListTour";

export default function SequencingGroupsPage({ user }) {
  const t = useT();
  const navigate = useNavigate();
  const [groups, setGroups]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [tourFlag, setTourFlag] = useState(0);
  const [tourOn, setTourOn]     = useState(false);

  async function refresh() {
    setLoading(true); setErr("");
    try {
      const data = await seqListGroups();
      setGroups(data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || String(e));
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  const tenantHint = useMemo(() => {
    // Best-effort: use the most-common tenant_id already in the list
    // to pre-fill the create form. Falls back to "" for admins.
    const counts = {};
    groups.forEach(g => { counts[g.tenant_id] = (counts[g.tenant_id] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || "";
  }, [groups]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="seq-groups-page">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center gap-3">
        <Link to="/sequencing" data-testid="seq-groups-back"
              className="w-8 h-8 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
          <ArrowLeft size={16} weight="bold" />
        </Link>
        <Stack size={20} weight="fill" className="text-blue-700 shrink-0" />
        <h1 data-tour="groups-header"
            className="flex-1 min-w-0 text-base md:text-lg font-bold font-['Chivo'] text-slate-900 truncate">
          {t("seq.groups_title", "Compressor groups")}
        </h1>
        <TourHelpButton
          testid="seq-groups-tour-help"
          scope="seq_groups"
          onClick={() => setTourFlag(f => f + 1)} />
        <Link
          to="/sequencing/groups/new"
          data-tour="groups-onboard-link"
          data-testid="seq-groups-onboard-link"
          className="text-xs font-bold uppercase tracking-wider px-3 h-9 rounded-sm border border-blue-200 text-blue-700 hover:bg-blue-50 inline-flex items-center gap-1.5"
        >
          <Lightning size={12} weight="fill" />
          <span className="hidden sm:inline">{t("seq.groups_onboard_wizard", "Onboard wizard")}</span>
        </Link>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          data-testid="seq-groups-create-open"
          className="text-xs font-bold uppercase tracking-wider px-3 h-9 rounded-sm bg-blue-700 text-white hover:bg-blue-800 inline-flex items-center gap-1.5"
        >
          <Plus size={12} weight="bold" /> {t("seq.groups_new", "New group")}
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-6">
        {err && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-sm">{err}</div>}

        {loading ? (
          <div className="py-10 text-center"><Spinner size={20} className="mx-auto animate-spin text-slate-400" /></div>
        ) : groups.length === 0 && !tourOn ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-sm p-8 text-center text-slate-500">
            <Stack size={28} weight="fill" className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm font-medium">{t("seq.groups_empty_title", "No groups yet")}</p>
            <p className="text-xs mt-1">{t("seq.groups_empty_hint", "Create your first compressor group to start sequencing.")}</p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="seq-groups-list">
            {tourOn && <DemoGroupRow />}
            {groups.map(g => (
              <li key={g.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/sequencing/groups/${g.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/sequencing/groups/${g.id}`);
                    }
                  }}
                  className="bg-white border border-slate-200 rounded-sm p-3 flex items-center gap-3 hover:border-blue-300 hover:bg-blue-50/30 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300"
                  data-testid={`seq-groups-row-${g.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-slate-900 truncate">{g.name}</div>
                  <div className="text-[11px] text-slate-500 truncate flex flex-wrap gap-x-3">
                    <span>tenant <span className="font-mono">{g.tenant_id}</span></span>
                    {g.common_pset != null && <span>Pset {g.common_pset} bar</span>}
                    <span>±{g.bandwidth_pct}%</span>
                    <span className={`uppercase tracking-wider font-bold ${g.state === "auto_sequencing" ? "text-emerald-700" : "text-slate-500"}`}>{g.state}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!window.confirm(`Delete group "${g.name}"?`)) return;
                    try {
                      await seqDeleteGroup(g.id);
                      toast.success(t("seq.groups_deleted", "Group deleted"));
                      refresh();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || err?.message);
                    }
                  }}
                  data-testid={`seq-groups-delete-${g.id}`}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-sm text-slate-400 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash size={14} weight="bold" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/sequencing/groups/${g.id}`);
                  }}
                  data-testid={`seq-groups-open-${g.id}`}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-sm text-slate-400 hover:text-blue-700 hover:bg-blue-50"
                >
                  <ArrowRight size={14} weight="bold" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </main>

      {showCreate && (
        <CreateGroupModal
          tenantHint={tenantHint}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); refresh(); }}
        />
      )}

      <GroupsListTour
        runFlag={tourFlag}
        onStart={() => setTourOn(true)}
        onStageChange={(stage) => setShowCreate(stage === "modal")}
        onStop={() => { setTourOn(false); setShowCreate(false); }}
      />
    </div>
  );
}

// Example row shown only while the guided tour is running so the tour
// always has a group-row anatomy to point at.
function DemoGroupRow() {
  return (
    <li data-tour="groups-example-row" data-testid="seq-groups-demo-row"
        className="bg-white border border-blue-200 rounded-sm p-3 flex items-center gap-3 relative">
      <span className="absolute -top-2 left-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-blue-700 text-white">
        example
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm text-slate-900 truncate">Plant A — Lead/Lag (example)</div>
        <div className="text-[11px] text-slate-500 truncate flex flex-wrap gap-x-3">
          <span>tenant <span className="font-mono">demo-tenant</span></span>
          <span>Pset 8 bar</span>
          <span>±5%</span>
          <span className="uppercase tracking-wider font-bold text-slate-500">configured</span>
        </div>
      </div>
      <span data-tour="groups-example-delete"
            className="w-8 h-8 inline-flex items-center justify-center rounded-sm text-slate-400">
        <Trash size={14} weight="bold" />
      </span>
      <span className="w-8 h-8 inline-flex items-center justify-center rounded-sm text-slate-400">
        <ArrowRight size={14} weight="bold" />
      </span>
    </li>
  );
}

function CreateGroupModal({ tenantHint, onClose, onCreated }) {
  const t = useT();
  const [name, setName]           = useState("");
  const [tenantId, setTenantId]   = useState(tenantHint || "");
  const [pset, setPset]           = useState("");
  const [bandwidth, setBandwidth] = useState("5");
  const [busy, setBusy]           = useState(false);

  async function submit(e) {
    e?.preventDefault?.();
    if (!name.trim() || !tenantId.trim()) return;
    setBusy(true);
    try {
      const g = await seqCreateGroup({
        name: name.trim(),
        tenant_id: tenantId.trim(),
        common_pset: pset === "" ? undefined : Number(pset),
        bandwidth_pct: Number(bandwidth) || 5.0,
      });
      toast.success(t("seq.groups_created", "Group created"));
      onCreated(g);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || String(e));
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-30 bg-black/50 flex items-end sm:items-center justify-center p-3"
         data-testid="seq-groups-create-modal">
      <form onSubmit={submit} data-tour="groups-create-form"
            className="bg-white rounded-sm w-full max-w-md p-5 space-y-3">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-bold font-['Chivo'] text-slate-900 flex-1">
            {t("seq.groups_new", "New group")}
          </h2>
          <button type="button" onClick={onClose} data-testid="seq-groups-create-close"
                  className="w-7 h-7 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-400">
            <X size={14} weight="bold" />
          </button>
        </div>

        <label className="block">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
            {t("seq.field_name", "Group name")}
          </span>
          <input value={name} onChange={e => setName(e.target.value)} required autoFocus
                 data-testid="seq-groups-field-name"
                 className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none" />
        </label>

        <label className="block">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
            {t("seq.field_tenant", "Tenant (CustomerID)")}
          </span>
          <input value={tenantId} onChange={e => setTenantId(e.target.value)} required
                 data-testid="seq-groups-field-tenant"
                 placeholder="e.g. e0c4b5fd"
                 className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm font-mono focus:border-blue-500 focus:outline-none" />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
              {t("seq.field_pset", "Pset (bar)")}
            </span>
            <input value={pset} onChange={e => setPset(e.target.value)} type="number" step="0.1"
                   data-testid="seq-groups-field-pset"
                   placeholder="8.0"
                   className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none" />
          </label>
          <label className="block">
            <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
              {t("seq.field_bandwidth", "Bandwidth (%)")}
            </span>
            <input value={bandwidth} onChange={e => setBandwidth(e.target.value)} type="number" step="0.1"
                   data-testid="seq-groups-field-bandwidth"
                   className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none" />
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} data-testid="seq-groups-create-cancel"
                  className="text-xs font-bold uppercase tracking-wider px-3 h-9 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50">
            {t("common.cancel", "Cancel")}
          </button>
          <button type="submit" disabled={busy || !name.trim() || !tenantId.trim()}
                  data-testid="seq-groups-create-submit"
                  className="text-xs font-bold uppercase tracking-wider px-4 h-9 rounded-sm bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 inline-flex items-center gap-1.5">
            {busy ? <Spinner size={12} className="animate-spin" /> : <Plus size={12} weight="bold" />}
            {t("seq.create", "Create")}
          </button>
        </div>
      </form>
    </div>
  );
}
